import React, { useState } from 'react';
import { T, font, mono, trackUrl } from '../utils/theme';
import * as api from '../utils/api';
import { Card, SBadge, SearchBar, Btn, IcEdit, IcCheck, IcMsg, IcPrint, matchesSearch, Modal } from './UI';
import { money } from '../utils/money';

function TrackingLink({ tracking, courier }) {
  const url = trackUrl(courier, tracking);
  if (!url || tracking === '—') return <span style={{ fontFamily: mono, fontSize: 12, color: T.muted }}>{tracking}</span>;
  return <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: mono, fontSize: 12, color: T.accent, textDecoration: 'underline' }}>{tracking}</a>;
}

export default function AdminPastOrders({ lang, products, allProducts, shipments, allSuppliers, shipmentComments, reload, Lk }) {
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [editQtyVal, setEditQtyVal] = useState('');
  const [openComment, setOpenComment] = useState(null);
  const [commentText, setCommentText] = useState('');

  // Print menu state
  const [printMenuId, setPrintMenuId] = useState(null); // shipment id whose print menu is open
  const [hoveredId, setHoveredId] = useState(null);

  // Add missed item state
  const [addItemModal, setAddItemModal] = useState(null); // shipment id
  const [addItemSearch, setAddItemSearch] = useState('');
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [addItemProduct, setAddItemProduct] = useState(null);
  const [addItemQty, setAddItemQty] = useState('1');
  const [addItemPrice, setAddItemPrice] = useState('');
  const [addItemSaving, setAddItemSaving] = useState(false);

  const received = shipments.filter(s => s.status === 'received');
  const prodLookup = (allProducts || products);

  const filtered = received.filter(s => {
    if (!search) return true;
    const sup = allSuppliers.find(x => x.id === s.supplier_id);
    const prods = (s.items || []).map(i => prodLookup.find(p => p.id === i.product_id)).filter(Boolean);
    return matchesSearch(search, s.tracking || '') || matchesSearch(search, sup?.name || '') || prods.some(p => matchesSearch(search, p.name) || matchesSearch(search, p.sku || ''));
  });

  const doEditReceivedQty = async (shipId, productId) => {
    if (!editQtyVal && editQtyVal !== '0') return;
    await api.editReceivedQty(shipId, productId, parseInt(editQtyVal));
    setEditingKey(null); setEditQtyVal('');
    await reload();
  };

  const doSendComment = async (shipId) => {
    if (!commentText.trim()) return;
    await api.addShipmentComment({ shipment_id: shipId, author: 'Admin', author_type: 'admin', text: commentText.trim() });
    setCommentText('');
    await reload();
  };

  const openAddItem = (shipId) => {
    setAddItemModal(shipId);
    setAddItemSearch('');
    setAddItemProduct(null);
    setAddItemQty('1');
    setAddItemPrice('');
    setShowAddDropdown(false);
  };

  const selectAddProduct = (p) => {
    setAddItemProduct(p);
    setAddItemSearch(p.name);
    setAddItemPrice(String(p.cost_price != null ? p.cost_price : ''));
    setShowAddDropdown(false);
  };

  const doAddItem = async () => {
    if (!addItemProduct || !addItemModal) return;
    setAddItemSaving(true);
    await api.addReceivedItem(addItemModal, {
      product_id: addItemProduct.id,
      qty: parseInt(addItemQty) || 1,
      unit_price: parseFloat(addItemPrice) || 0,
    });
    setAddItemModal(null);
    setAddItemSaving(false);
    await reload();
  };

  const doPrintOrder = (s) => {
    const sup = allSuppliers.find(x => x.id === s.supplier_id);
    const items = (s.items || []).map(i => {
      const p = prodLookup.find(x => x.id === i.product_id);
      return p ? { name: p.deleted ? `${p.name} (Deleted)` : p.name, sku: p.sku, qty: i.ship_qty || 0, price: i.unit_price || p.cost_price || 0 } : null;
    }).filter(Boolean);
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>Order – ${s.tracking}</title><style>body{font-family:Arial,sans-serif;padding:40px;color:#333}table{width:100%;border-collapse:collapse;margin-top:20px}th{background:#222;color:#fff;padding:10px 14px;text-align:left;font-size:13px}td{padding:8px 14px;border-bottom:1px solid #ddd;font-size:13px}.header{display:flex;justify-content:space-between;margin-bottom:30px}.info{text-align:right;font-size:12px;line-height:1.6}.total{text-align:right;padding:10px 14px;font-weight:bold;font-size:14px}</style></head><body>`);
    w.document.write(`<div class="header"><div><strong>Tan Star Trade</strong></div><div class="info"><strong>9264-9854 Quebec Inc.</strong><br>8500 Boulevard Taschereau, Unit 10<br>Brossard, Quebec, J4X 2T4, Canada<br>(450)-465-5506</div></div>`);
    w.document.write(`<div style="margin-bottom:10px"><strong>Received:</strong> ${s.received_date || s.created_at} | <strong>Courier:</strong> ${s.courier} | <strong>From:</strong> ${sup?.name} | <strong>Tracking:</strong> ${s.tracking}</div>`);
    w.document.write(`<table><thead><tr><th>Product</th><th>SKU</th><th>Qty</th><th>Unit Price</th><th>Subtotal</th></tr></thead><tbody>`);
    let grandTotal = 0;
    items.forEach(p => { const sub = p.price * p.qty; grandTotal += sub; w.document.write(`<tr><td>${p.name}</td><td>${p.sku || '—'}</td><td>${p.qty}</td><td>${money(p.price)}</td><td>${money(sub)}</td></tr>`); });
    w.document.write(`</tbody></table>`);
    w.document.write(`<div class="total">Subtotal: ${money(grandTotal)}</div>`);
    if (s.shipping_cost) w.document.write(`<div class="total">Shipping: ${money(s.shipping_cost)}</div>`);
    w.document.write(`<div class="total" style="font-size:16px;border-top:2px solid #333;margin-top:4px;padding-top:8px">Total: ${money((grandTotal + (s.shipping_cost || 0)))}</div>`);
    w.document.write(`</body></html>`);
    w.document.close();
    w.print();
  };

  const doPrintPriceChanges = async (s) => {
    setPrintMenuId(null);
    const date = s.received_date || s.created_at;
    const result = await api.getShipmentPriceChanges(s.id);
    const { price_changes, new_products } = result;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>Price Update Summary – ${date}</title><style>
      body{font-family:Arial,sans-serif;padding:40px;color:#000;font-size:13px}
      h1{font-size:18px;margin:0 0 4px}
      .sub{font-size:12px;color:#555;margin-bottom:30px}
      h2{font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:#333;margin:24px 0 8px;padding-bottom:4px;border-bottom:2px solid #000}
      table{width:100%;border-collapse:collapse;margin-bottom:16px}
      th{background:#eee;padding:7px 10px;font-size:11px;font-weight:800;text-transform:uppercase;color:#333;border-bottom:1px solid #999}
      th.l{text-align:left} th.r{text-align:right}
      td{padding:7px 10px;border-bottom:1px solid #ddd;font-size:13px}
      td.l{text-align:left} td.r{text-align:right;font-family:monospace}
      td.name{font-weight:600} td.newprice{font-weight:800;font-size:14px;font-family:monospace;text-align:right}
      td.change{font-family:monospace;text-align:right;font-weight:700}
      @media print{body{padding:20px}}
    </style></head><body>`);
    w.document.write(`<h1>Price Update Summary</h1><div class="sub">Received: ${date} &nbsp;·&nbsp; Tan Star Trade</div>`);
    if (price_changes.length > 0) {
      w.document.write(`<h2>Products with Price Changes (${price_changes.length})</h2>`);
      w.document.write(`<table><thead><tr><th class="l">Product</th><th class="r">New Price</th><th class="r">Previous Price</th><th class="r">Change</th></tr></thead><tbody>`);
      price_changes.forEach(p => {
        const diff = p.new_price - p.previous_price;
        const changeStr = diff >= 0 ? `+${money(diff)}` : `-${money(Math.abs(diff))}`;
        w.document.write(`<tr><td class="name l">${p.product_name}</td><td class="newprice">${money(p.new_price)}</td><td class="r">${money(p.previous_price)}</td><td class="change">${changeStr}</td></tr>`);
      });
      w.document.write(`</tbody></table>`);
    }
    if (new_products.length > 0) {
      w.document.write(`<h2>New Products – First Time Purchased (${new_products.length})</h2>`);
      w.document.write(`<table><thead><tr><th class="l">Product</th><th class="r">New Price</th></tr></thead><tbody>`);
      new_products.forEach(p => {
        w.document.write(`<tr><td class="name l">${p.product_name}</td><td class="newprice">${money(p.new_price)}</td></tr>`);
      });
      w.document.write(`</tbody></table>`);
    }
    if (price_changes.length === 0 && new_products.length === 0) {
      w.document.write(`<p style="color:#555;font-style:italic">No price changes or new products found for this order.</p>`);
    }
    w.document.write(`</body></html>`);
    w.document.close();
    w.print();
  };

  const getProductName = (productId) => {
    const p = prodLookup.find(x => x.id === productId);
    if (!p) return productId;
    return p.deleted ? `${p.name} (Deleted)` : p.name;
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 22px', fontSize: 22, fontWeight: 900, color: T.text }}>{Lk('pastOrders')} ({received.length})</h2>
      <SearchBar sticky value={search} onChange={setSearch} placeholder={`${Lk('searchProduct')} / ${Lk('searchTracking')} / ${Lk('searchSku')} / ${Lk('searchSupplier')}`} style={{ marginBottom: 16 }} />

      {filtered.length === 0 ? (
        <div style={{ color: T.dim, fontSize: 13, fontStyle: 'italic', padding: 20 }}>{search ? Lk('noResults') : Lk('noData')}</div>
      ) : filtered.map(s => {
        const sup = allSuppliers.find(x => x.id === s.supplier_id);
        const isOpen = expanded[s.id];
        const hasMismatch = (s.items || []).some(i => i.received && i.received_qty !== i.ship_qty);
        const comments = (shipmentComments || []).filter(c => c.shipment_id === s.id);
        const hasComments = comments.length > 0;
        const isCommentOpen = openComment === s.id;

        return (
          <Card key={s.id} hoverBg={!hasMismatch} style={{ marginBottom: 10, cursor: 'pointer', borderColor: hasMismatch ? T.orange + '80' : T.border, background: hasMismatch ? T.orangeBg : T.surface }}
            onClick={() => { if (printMenuId) { setPrintMenuId(null); return; } setExpanded({ ...expanded, [s.id]: !isOpen }); }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 14, color: T.dim }}>{isOpen ? '▼' : '▶'}</span>
                <span style={{ fontSize: 11, color: T.dim, fontFamily: mono }}>{s.received_date || s.created_at}</span>
                <span style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>{sup?.name}</span>
                <TrackingLink tracking={s.tracking} courier={s.courier} />
                {s.courier && s.courier !== '—' && <SBadge label={s.courier} color={T.muted} bg={T.surfaceAlt} />}
                <span style={{ fontSize: 11, color: T.dim }}>{(s.items || []).length} items</span>
                {hasComments && <span style={{ width: 10, height: 10, borderRadius: 5, background: T.warn, display: 'inline-block', flexShrink: 0 }} title="Has comments" />}
                {hasMismatch && <SBadge label={Lk('mismatch')} color={T.orange} bg={T.orangeBg} />}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: T.dim }}>{Lk('received')} {s.received_date}</span>
                <SBadge label={Lk('received')} color={T.success} bg={T.successBg} />
                <span onClick={e => { e.stopPropagation(); setOpenComment(isCommentOpen ? null : s.id); setCommentText(''); }} style={{ cursor: 'pointer' }}>{IcMsg}</span>
              </div>
            </div>

            {isOpen && (
              <div style={{ marginTop: 12 }} onClick={e => e.stopPropagation()}>
                {(() => {
                  let subtotal = 0;
                  const rows = (s.items || []).map(item => {
                    const p = prodLookup.find(x => x.id === item.product_id);
                    const mismatch = item.received_qty !== item.ship_qty;
                    const key = `${s.id}-${item.product_id}`;
                    const isEditingThis = editingKey === key;
                    const price = item.unit_price || p?.quotes?.[s.supplier_id]?.price || p?.cost_price || 0;
                    const qty = item.ship_qty || 0;
                    const lineSub = price * qty;
                    subtotal += lineSub;
                    return (
                      <tr key={item.product_id} style={{ background: mismatch ? T.orangeBg : hoveredId === `${s.id}-${item.product_id}` ? '#EFF6FF' : 'transparent' }} onMouseEnter={() => setHoveredId(`${s.id}-${item.product_id}`)} onMouseLeave={() => setHoveredId(null)}>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}`, color: T.text, fontWeight: 600 }}>{getProductName(item.product_id)}</td>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.muted, fontSize: 11 }}>{p?.sku || '—'}</td>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: mono }}>{qty}</td>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.muted }}>{money(price)}</td>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.text }}>{money(lineSub)}</td>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}` }}>
                          {isEditingThis ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <input type="number" value={editQtyVal} onChange={e => setEditQtyVal(e.target.value)} autoFocus
                                onKeyDown={e => { if (e.key === 'Enter') doEditReceivedQty(s.id, item.product_id); if (e.key === 'Escape') setEditingKey(null); }}
                                style={{ fontFamily: mono, fontSize: 12, padding: '3px 6px', width: 55, background: T.bg, border: `1px solid ${T.accent}`, borderRadius: 4, color: T.text, outline: 'none' }} />
                              <Btn variant="small" style={{ padding: '3px 6px', fontSize: 10 }} onClick={() => doEditReceivedQty(s.id, item.product_id)}>{IcCheck}</Btn>
                              <Btn variant="ghost" style={{ padding: '3px 6px', fontSize: 10 }} onClick={() => setEditingKey(null)}>✕</Btn>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontFamily: mono, color: mismatch ? T.orange : T.success }}>
                                {item.received_qty} {mismatch && <SBadge label={Lk('mismatch')} color={T.orange} bg={T.orangeBg} />}
                              </span>
                              <Btn variant="ghostSm" style={{ padding: '3px 8px', fontSize: 10 }} onClick={() => { setEditingKey(key); setEditQtyVal(String(item.received_qty || '')); }}>{IcEdit}</Btn>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  });
                  const total = subtotal + (s.shipping_cost || 0);
                  return (
                    <>
                      <table style={{ width: '100%', fontSize: 12, fontFamily: font, borderCollapse: 'collapse' }}>
                        <thead><tr>
                          {[Lk('productName'), Lk('sku'), lang === 'zh' ? '发货' : 'Shipped', lang === 'zh' ? '单价' : 'Unit Price', 'Subtotal', lang === 'zh' ? '收货 / 操作' : 'Received / Actions'].map((h, i) => (
                            <th key={i} style={{ textAlign: 'left', padding: '6px 10px', background: T.surfaceAlt, color: T.dim, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', borderBottom: `1px solid ${T.border}` }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>{rows}</tbody>
                      </table>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, padding: '6px 0', borderTop: `1px solid ${T.border}30` }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <Btn variant="ghostSm" style={{ fontSize: 11 }} onClick={() => openAddItem(s.id)}>
                            + {lang === 'zh' ? '添加漏收商品' : 'Add missed item'}
                          </Btn>
                          {/* Print dropdown */}
                          <div style={{ position: 'relative' }}>
                            <Btn variant="ghostSm" style={{ fontSize: 11 }} onClick={() => setPrintMenuId(printMenuId === s.id ? null : s.id)}>
                              {IcPrint} {lang === 'zh' ? '打印' : 'Print'} ▾
                            </Btn>
                            {printMenuId === s.id && (
                              <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 200, overflow: 'hidden', marginTop: 4 }}>
                                <div
                                  onClick={() => { setPrintMenuId(null); doPrintOrder(s); }}
                                  style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer', color: T.text, borderBottom: `1px solid ${T.border}` }}
                                  onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
                                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                  {IcPrint} {lang === 'zh' ? '打印订单' : 'Print Order'}
                                </div>
                                <div
                                  onClick={() => doPrintPriceChanges(s)}
                                  style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer', color: T.text }}
                                  onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
                                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                  {IcPrint} {lang === 'zh' ? '打印价格变动' : 'Print Price Changes'}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 16 }}>
                          <span style={{ fontSize: 11, color: T.dim }}>Subtotal: <span style={{ fontFamily: mono, color: T.muted }}>{money(subtotal)}</span></span>
                          <span style={{ fontSize: 11, color: T.dim }}>{lang === 'zh' ? '运费' : 'Shipping'}: <span style={{ fontFamily: mono, color: T.muted }}>{money((s.shipping_cost || 0))}</span></span>
                          <span style={{ fontSize: 12, fontWeight: 800, color: T.accent }}>Total: <span style={{ fontFamily: mono }}>{money(total)}</span></span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Comment section */}
            {isCommentOpen && (
              <div style={{ marginTop: 10, padding: 12, background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
                <div style={{ fontSize: 10, fontWeight: 800, color: T.dim, textTransform: 'uppercase', marginBottom: 8 }}>{lang === 'zh' ? '物流备注' : 'Shipment Notes'}</div>
                {comments.map(c => (
                  <div key={c.id} style={{ fontSize: 12, padding: '4px 0', borderBottom: `1px solid ${T.border}20` }}>
                    <span style={{ fontWeight: 700, color: c.author_type === 'admin' ? T.accent : T.success }}>{c.author}: </span>
                    <span style={{ color: T.text }}>{c.text}</span>
                    <span style={{ color: T.dim, fontSize: 10, marginLeft: 6 }}>{c.created_at?.split('T')[0]}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder={lang === 'zh' ? '添加备注...' : 'Add note...'}
                    onKeyDown={e => { if (e.key === 'Enter') doSendComment(s.id); }}
                    style={{ flex: 1, fontFamily: font, fontSize: 12, padding: '7px 10px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none' }} />
                  <Btn variant="small" style={{ padding: '5px 10px', fontSize: 11 }} onClick={() => doSendComment(s.id)}>{lang === 'zh' ? '发送' : 'Send'}</Btn>
                </div>
              </div>
            )}
          </Card>
        );
      })}

      {/* Add missed item modal */}
      <Modal open={!!addItemModal} onClose={() => !addItemSaving && setAddItemModal(null)}>
        <div>
          <div style={{ fontWeight: 900, fontSize: 17, color: T.text, marginBottom: 16 }}>
            {lang === 'zh' ? '添加漏收商品' : 'Add Missed Item'}
          </div>

          {/* Product search */}
          <div style={{ position: 'relative', marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.dim, textTransform: 'uppercase', marginBottom: 5 }}>
              {lang === 'zh' ? '商品' : 'Product'}
            </div>
            <input
              type="text"
              value={addItemSearch}
              onChange={e => { setAddItemSearch(e.target.value); setAddItemProduct(null); setShowAddDropdown(true); }}
              onFocus={() => setShowAddDropdown(true)}
              onBlur={() => setTimeout(() => setShowAddDropdown(false), 150)}
              placeholder={lang === 'zh' ? '搜索商品…' : 'Search products…'}
              disabled={addItemSaving}
              style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, padding: '8px 12px', background: T.bg, border: `1px solid ${addItemProduct ? T.success : T.border}`, borderRadius: 6, color: T.text, outline: 'none', boxSizing: 'border-box' }}
            />
            {addItemProduct && (
              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-20%)', color: T.success, fontSize: 14 }}>{IcCheck}</span>
            )}
            {showAddDropdown && addItemSearch.length > 0 && !addItemProduct && (() => {
              const shipment = filtered.find(s => s.id === addItemModal);
              const existingIds = new Set((shipment?.items || []).map(i => i.product_id));
              const results = (allProducts || []).filter(p => {
                if (p.deleted) return false;
                if (existingIds.has(p.id)) return false;
                return matchesSearch(addItemSearch, p.name) || matchesSearch(addItemSearch, p.sku || '');
              }).slice(0, 8);
              return (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 200, maxHeight: 220, overflowY: 'auto' }}>
                  {results.length === 0 ? (
                    <div style={{ padding: '10px 12px', fontSize: 13, color: T.dim, fontStyle: 'italic' }}>
                      {lang === 'zh' ? '未找到商品' : 'No products found'}
                    </div>
                  ) : results.map(p => (
                    <div key={p.id} onMouseDown={() => selectAddProduct(p)}
                      style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: `1px solid ${T.border}`, display: 'flex', gap: 8, alignItems: 'center' }}
                      onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: T.text, flex: 1 }}>{p.name}</span>
                      <span style={{ fontSize: 11, color: T.dim, fontFamily: mono }}>{p.sku}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Qty + Price */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.dim, textTransform: 'uppercase', marginBottom: 5 }}>{lang === 'zh' ? '数量' : 'Qty'}</div>
              <input type="number" value={addItemQty} onChange={e => setAddItemQty(e.target.value)} disabled={addItemSaving}
                style={{ width: '100%', fontFamily: mono, fontSize: 14, padding: '8px 12px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none', boxSizing: 'border-box', textAlign: 'center' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.dim, textTransform: 'uppercase', marginBottom: 5 }}>{lang === 'zh' ? '单价（可选）' : 'Unit Price (optional)'}</div>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: T.dim, fontSize: 13, pointerEvents: 'none' }}>$</span>
                <input type="number" step="0.01" value={addItemPrice} onChange={e => setAddItemPrice(e.target.value)} disabled={addItemSaving}
                  style={{ width: '100%', fontFamily: mono, fontSize: 14, padding: '8px 12px 8px 22px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setAddItemModal(null)} disabled={addItemSaving}>{Lk('cancel')}</Btn>
            <Btn onClick={doAddItem} disabled={!addItemProduct || addItemSaving}>
              {addItemSaving ? (lang === 'zh' ? '保存中…' : 'Saving…') : (lang === 'zh' ? '确认添加' : 'Add Item')}
            </Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
